import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { MODAL_REF, ModalRef } from './modal-ref.token';

export interface ModalOptions {
  component: Type<unknown>;
  componentProps?: Record<string, unknown>;
  cssClass?: string;
  backdropDismiss?: boolean;
}

interface ActiveModal {
  backdropEl: HTMLElement;
  containerEl: HTMLElement;
  componentRef: ComponentRef<unknown>;
  resolve: (data: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class ModalService {
  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private envInjector: EnvironmentInjector,
  ) {}

  async create(options: ModalOptions): Promise<{ present(): Promise<void>; onDidDismiss(): Promise<{ data: unknown }>; dismiss(data?: unknown): void }> {
    let dismissFn: (data?: unknown) => void;
    const dismissPromise = new Promise<{ data: unknown }>((resolve) => {
      const modal: ActiveModal = this.buildModal(options, (data) => {
        this.destroyModal(modal);
        resolve({ data });
      });
      dismissFn = (data) => modal.resolve(data);

      (this as any)._pendingModal = modal;
    });

    return {
      present: async () => {
        const modal = (this as any)._pendingModal as ActiveModal;
        if (modal) {
          document.body.appendChild(modal.backdropEl);
          document.body.appendChild(modal.containerEl);
          requestAnimationFrame(() => {
            modal.backdropEl.classList.add('modal-backdrop-visible');
            modal.containerEl.classList.add('modal-container-visible');
          });
        }
      },
      onDidDismiss: () => dismissPromise,
      dismiss: (data?: unknown) => dismissFn(data),
    };
  }

  private buildModal(options: ModalOptions, onDismiss: (data: unknown) => void): ActiveModal {
    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    if (options.backdropDismiss !== false) {
      backdrop.addEventListener('click', () => onDismiss(undefined));
    }

    // Container
    const container = document.createElement('div');
    container.className = 'modal-container';
    if (options.cssClass) {
      options.cssClass.split(' ').forEach(c => container.classList.add(c));
    }

    const modalRef: ModalRef = { dismiss: (data?: unknown) => onDismiss(data) };

    const childInjector = Injector.create({
      providers: [{ provide: MODAL_REF, useValue: modalRef }],
      parent: this.injector,
    });

    const componentRef = createComponent(options.component, {
      environmentInjector: this.envInjector,
      elementInjector: childInjector,
      hostElement: container,
    });

    // Set @Input() properties
    if (options.componentProps) {
      Object.entries(options.componentProps).forEach(([key, value]) => {
        (componentRef.instance as Record<string, unknown>)[key] = value;
      });
    }

    this.appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();

    return { backdropEl: backdrop, containerEl: container, componentRef, resolve: onDismiss };
  }

  private destroyModal(modal: ActiveModal): void {
    modal.backdropEl.classList.remove('modal-backdrop-visible');
    modal.containerEl.classList.remove('modal-container-visible');

    setTimeout(() => {
      this.appRef.detachView(modal.componentRef.hostView);
      modal.componentRef.destroy();
      modal.backdropEl.remove();
      modal.containerEl.remove();
    }, 200);
  }
}
